import { Dialog as DialogPrimitive, Popover as PopoverPrimitive } from 'radix-ui';
import { useId, type ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { useIsPhone } from '../lib/use-media-query';
import { IconButton } from './button';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverClose = PopoverPrimitive.Close;

export const popoverPanelClasses =
  'z-50 rounded-lg border border-line bg-raised text-fg shadow-overlay outline-none data-[state=closed]:animate-fade-out data-[state=open]:animate-pop-in';

export interface PopoverContentProps {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  'aria-label'?: string;
}

export function PopoverContent({
  children,
  align = 'center',
  side = 'bottom',
  className,
  'aria-label': ariaLabel,
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={8}
        aria-label={ariaLabel}
        className={cn(popoverPanelClasses, 'w-72 p-3', className)}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

export interface ResponsivePopoverProps {
  /** The button that opens the panel. Must accept a ref (e.g. `IconButton`, `Button`). */
  trigger: ReactNode;
  title: ReactNode;
  /** Actions in the panel header, e.g. "mark all as read". */
  headerActions?: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
}

/**
 * Panel of the top bar (sync status, notifications, profile): a dropdown panel on tablet and
 * desktop, a bottom sheet on the phone (docs/ui/responsive.md, Shell).
 */
export function ResponsivePopover({
  trigger,
  title,
  headerActions,
  children,
  open,
  onOpenChange,
  align = 'end',
  className,
}: ResponsivePopoverProps) {
  const labels = useUiLabels();
  const phone = useIsPhone();
  const titleId = useId();

  if (phone) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 flex bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in">
            <DialogPrimitive.Content
              aria-describedby={undefined}
              className="relative mt-auto flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-lg bg-raised pb-safe shadow-overlay outline-none data-[state=closed]:animate-sheet-down data-[state=open]:animate-sheet-up"
            >
              <div
                aria-hidden
                className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong"
              />
              <div className="flex items-center gap-2 px-4 pt-2 pb-2">
                <DialogPrimitive.Title className="min-w-0 flex-1 text-base font-semibold">
                  {title}
                </DialogPrimitive.Title>
                {headerActions}
                <DialogPrimitive.Close asChild>
                  <IconButton icon="x" label={labels.close} size="sm" tooltip={false} />
                </DialogPrimitive.Close>
              </div>
              <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>{children}</div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          sideOffset={8}
          collisionPadding={8}
          aria-labelledby={titleId}
          className={cn(
            popoverPanelClasses,
            'flex max-h-(--radix-popover-content-available-height) w-96 flex-col overflow-hidden',
          )}
        >
          <div className="flex min-h-12 items-center gap-2 border-b border-line px-4 py-2">
            <h2 id={titleId} className="min-w-0 flex-1 text-sm font-semibold">
              {title}
            </h2>
            {headerActions}
          </div>
          <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>{children}</div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
